import React, { useState, useEffect, useRef, useCallback } from 'react';
import RecommendationList from './RecomendationList';
import { GoogleMap, LoadScriptNext, Marker, InfoWindow } from '@react-google-maps/api';
import Split from 'react-split';
import { Star, MapPin, Plus, Map, List, Search, Filter, X } from 'lucide-react';
import useRecommendations from '../../api/recommendations';
import axios from 'axios';
import AddRecommendationModal from './AddRecommendationModal';
import Loader from '../ui/loader';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useTranslation } from 'react-i18next';
import { useSyncedUser } from '../../context/SyncedUserContext';

const libraries = ['places'];

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const center = {
  lat: 51.2289,
  lng: 2.9097,
};

const DiscoverPage = () => {
  const { t } = useTranslation();
  const { syncedUserData } = useSyncedUser();
  const [recommendations, setRecommendations] = useState([]);
  const [ratings, setRatings] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showMap, setShowMap] = useState(false); // For mobile view switching
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const { category } = useParams();
  const navigate = useNavigate();
  const recommendationsApi = useRecommendations();

  // Detect if we're on mobile
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    // Handle window resize for mobile detection
    const handleResize = () => {
      setShowMap(window.innerWidth >= 768);
    };
    
    // Initialize state based on current screen size
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchRecommendations = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const apiInstance = axios.create({
        baseURL: process.env.REACT_APP_BACKEND_URL + "api/recommendations",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      const data = await apiInstance.get("/");
      setRecommendations(data.data.recommendations);

      // Fetch ratings and likes for each recommendation
      const ratingPromises = data.data.recommendations.map(async (rec) => {
        const rating = await apiInstance.get(`/${rec.id}/rating?userId=${syncedUserData?.user?.id}`);
        return { id: rec.id, rating: rating.data };
      });
      const ratingResults = await Promise.all(ratingPromises);
      
      // Create ratings map and sync favorites
      const ratingMap = {};
      const likes = [];
      ratingResults.forEach(({ id, rating }) => {
        ratingMap[id] = rating;
        console.log(`Rating for ${id}:`, rating);
        if (rating.isLiked) {
          likes.push(id);
        }
      });
      
      console.log('Final rating map:', ratingMap);
      console.log('Final likes array:', likes);
      
      setRatings(ratingMap);
      setFavorites(likes);
      setLoading(false);
    } catch (error) {
      console.error(t('discover.errors.fetchFailed'), error);
      setLoading(false);
    }
  }, [t, syncedUserData?.user?.id]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  const toggleFavorite = useCallback(async (id) => {
    try {
      const token = localStorage.getItem("access_token");
      const apiInstance = axios.create({
        baseURL: process.env.REACT_APP_BACKEND_URL + "api/recommendations",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      const result = await apiInstance.post(`/${id}/like`, { userId: syncedUserData?.user?.id });
      
      console.log('Toggle like result:', result.data);
      
      // Update favorites array
      if (result.data.liked) {
        setFavorites(prev => [...prev, id]);
      } else {
        setFavorites(prev => prev.filter(favId => favId !== id));
      }
      
      // Update ratings data
      setRatings(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          isLiked: result.data.liked,
          likesCount: result.data.likesCount
        }
      }));
      
      // Force refresh the rating data to ensure consistency
      try {
        const ratingResponse = await apiInstance.get(`/${id}/rating?userId=${syncedUserData?.user?.id}`);
        console.log('Rating response after toggle:', ratingResponse.data);
        setRatings(prev => ({
          ...prev,
          [id]: ratingResponse.data
        }));
      } catch (error) {
        console.error('Error fetching updated rating:', error);
      }
    } catch (error) {
      console.error(t('discover.errors.likeFailed'), error);
    }
  }, [syncedUserData?.user?.id, t]);

  const filteredRecommendations = recommendations.filter(rec => 
    activeFilter === 'all' ? true :
    activeFilter === 'favorites' ? favorites.includes(rec.id) :
    rec.type.toLowerCase() === activeFilter
  );

  const handleMarkerClick = (recommendation) => {
    setSelectedMarker(recommendation);
  };

  useEffect(() => {
    if (category) {
      setActiveFilter(category);
    }
  }, [category]);

  const handleFilterChange = (newFilter) => {
    setActiveFilter(newFilter);
    
    // Only update the route, don't remount the component (which would reset state)
    if (newFilter === 'all') {
      if (window.location.pathname !== '/discover') {
        navigate('/discover', { replace: true });  // Use replace to avoid building up history
      }
    } else {
      if (window.location.pathname !== `/discover/${newFilter}`) {
        navigate(`/discover/${newFilter}`, { replace: true });
      }
    }
  };

  // Mobile view toggle buttons
  const MobileViewToggle = () => (
    <div className="fixed bottom-20 right-4 z-30 flex shadow-lg rounded-full overflow-hidden">
      <button 
        className={`p-3 ${!showMap ? 'bg-primary text-white' : 'bg-white dark:bg-neutral-800'}`}
        onClick={() => setShowMap(false)}
        aria-label={t('discover.accessibility.listView')}
      >
        <List className="h-5 w-5" />
      </button>
      <button 
        className={`p-3 ${showMap ? 'bg-primary text-white' : 'bg-white dark:bg-neutral-800'}`}
        onClick={() => setShowMap(true)}
        aria-label={t('discover.accessibility.mapView')}
      >
        <Map className="h-5 w-5" />
      </button>
    </div>
  );

  // Desktop view with Split
  const DesktopView = () => (
    <Split 
      className="h-screen flex split"
      sizes={[60, 40]}
      minSize={300}
      expandToMin={false}
      gutterSize={10}
      gutterAlign="center"
      snapOffset={30}
      dragInterval={1}
      direction="horizontal"
    >
      <div className="w-3/5 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
            <Loader />
          </div>
        )}
        <RecommendationList 
          recommendations={recommendations}
          ratings={ratings}
          onHoverItem={setHoveredItem}
          selectedItem={selectedMarker}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          activeFilter={activeFilter}
          setActiveFilter={handleFilterChange}
          onRefresh={fetchRecommendations}
          onAddClick={() => setIsAddModalOpen(true)}
        />
      </div>
      <div className="w-2/5 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
            <Loader />
          </div>
        )}
        <LoadScriptNext 
          googleMapsApiKey={process.env.REACT_APP_GOOGLE_API} 
          libraries={libraries}
        >
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={14}
          >
            {filteredRecommendations.map((rec) => (
              <Marker
                key={rec.id}
                position={{ lat: Number(rec.lat), lng: Number(rec.lng) }}
                onClick={() => handleMarkerClick(rec)}
              >
                {(hoveredItem?.id === rec.id || selectedMarker?.id === rec.id) && (
                  <InfoWindow
                    position={{ lat: Number(rec.lat), lng: Number(rec.lng) }}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <div className="p-4 min-w-[200px]">
                      <h3 className="font-semibold text-lg mb-2">{rec.title}</h3>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 bg-neutral-100 rounded-md text-sm">
                          {rec.type}
                        </span>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < rec.rating
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'fill-gray-200 text-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {rec.description}
                      </p>
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-1" />
                        {rec.address}
                      </div>
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            ))}
          </GoogleMap>
        </LoadScriptNext>
      </div>
    </Split>
  );

  // Mobile view with conditional rendering
  const MobileView = () => (
    <div className="h-screen relative">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
          <Loader />
        </div>
      )}
      
      {showMap ? (
        // Map View (full screen on mobile)
        <div className="h-screen">
          <LoadScriptNext 
            googleMapsApiKey={process.env.REACT_APP_GOOGLE_API} 
            libraries={libraries}
          >
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={14}
            >
              {filteredRecommendations.map((rec) => (
                <Marker
                  key={rec.id}
                  position={{ lat: Number(rec.lat), lng: Number(rec.lng) }}
                  onClick={() => handleMarkerClick(rec)}
                >
                  {selectedMarker?.id === rec.id && (
                    <InfoWindow
                      position={{ lat: Number(rec.lat), lng: Number(rec.lng) }}
                      onCloseClick={() => setSelectedMarker(null)}
                    >
                      <div className="p-3 min-w-[180px]">
                        <h3 className="font-semibold text-base mb-1">{rec.title}</h3>
                        <div className="flex items-center gap-1 mb-1">
                          <span className="px-1.5 py-0.5 bg-neutral-100 rounded-md text-xs">
                            {rec.type}
                          </span>
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-3 h-3 ${
                                  i < rec.rating
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'fill-gray-200 text-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mb-1 line-clamp-2">
                          {rec.description}
                        </p>
                        <div className="flex items-center text-xs text-gray-600">
                          <MapPin className="w-3 h-3 mr-1" />
                          <span className="truncate">{rec.address}</span>
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
              ))}
            </GoogleMap>
          </LoadScriptNext>
        </div>
      ) : (
        // List View (full screen on mobile)
        <RecommendationList 
          recommendations={recommendations}
          ratings={ratings}
          onHoverItem={setHoveredItem}
          selectedItem={selectedMarker}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          activeFilter={activeFilter}
          setActiveFilter={handleFilterChange}
          onRefresh={fetchRecommendations}
          onAddClick={() => setIsAddModalOpen(true)}
        />
      )}
      
      <MobileViewToggle />
    </div>
  );

  return (
    <div className="space-y-4 pb-16 md:pb-6 overflow-y-auto max-h-[calc(100dvh)]">
      <div className="hidden md:block">
        <DesktopView />
      </div>
      
      <div className="block md:hidden">
        <MobileView />
      </div>

      <AddRecommendationModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={fetchRecommendations}
      />
    </div>
  );
};

export default DiscoverPage;